import { Location } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Dictionary } from '@ngrx/entity';
import { Action, select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, withLatestFrom } from 'rxjs/operators';
import { departmentsRoute } from 'src/app/app-routing.module';
import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { LoadPageSuccessProps } from 'src/app/common-components/register/models/register-actions';
import { EntityRegisterView } from 'src/app/common-components/register/models/register-view';
import { DepartmentPrivileges } from '../models/department-privileges';
import { DepartmentOrderingOptions, DepartmentRegisterOptions } from '../models/department-register-options';
import { DepartmentRegisterListItem } from '../models/department-register-results.model';
import * as departmentRegisterActions from '../state/department-register.actions';
import { selectQuickFilters } from '../state/department-register.reducer';
import * as departmentRegisterSelectors from '../state/department-register.selectors';

@Component({
   selector: 'ndt-department-register-view',
   styleUrls: ['./department-register-view.component.scss'],
   templateUrl: './department-register-view.component.html',
   standalone: false,
})
export class DepartmentRegisterViewComponent
   extends EntityRegisterView<DepartmentRegisterListItem, DepartmentRegisterOptions, DepartmentOrderingOptions>
   implements OnDestroy, AfterViewInit
{
   protected selectQuickFilters(): (state: object) => DepartmentRegisterOptions | null {
      return selectQuickFilters;
   }
   @Output() selectedId = new EventEmitter<number>();

   @Output() deleteDepartment = new EventEmitter<DepartmentRegisterListItem>();

   @Output() deactivateDepartment = new EventEmitter<DepartmentRegisterListItem>();

   @Output() renameDepartment = new EventEmitter<DepartmentRegisterListItem>();

   @Output() mergeDepartment = new EventEmitter<DepartmentRegisterListItem>();

   @Output() untagDepartment = new EventEmitter<DepartmentRegisterListItem>();

   @Input() departmentPrivileges: DepartmentPrivileges;

   pluralName = 'Departments';

   currentSelectedId: number;

   includeInactive: boolean = false;

   private destroyed$ = new Subject<void>();

   constructor(
      private twoColumnLayoutService: TwoColumnLayoutService,
      public store: Store,
      protected route: ActivatedRoute,
      private actions$: Actions,
      private location: Location,
      private router: Router,
      private cdr: ChangeDetectorRef
   ) {
      super(store);

      this.twoColumnLayoutService.selectedId$.pipe(takeUntil(this.destroyed$)).subscribe((selectedId) => {
         if (selectedId) {
            this.currentSelectedId = selectedId;
         }
      });
   }

   @Input()
   getRouterLink(id: number) {
      return `/${departmentsRoute}/${id}`;
   }

   ngOnDestroy(): void {
      this.twoColumnLayoutService.setSelectedId(null);
      this.destroyed$.next();
   }

   ngAfterViewInit(): void {
      let id = this.route.snapshot.params['id'];
      if (id) {
         if (isNaN(id)) {
            this.router.navigate(['/not-found'], { skipLocationChange: true });
            return;
         }
         id = +id;
         this.actions$
            .pipe(
               ofType(
                  departmentRegisterActions.departmentRegisterAdapterActions.loadSuccess,
                  departmentRegisterActions.departmentRegisterAdapterActions.loadPageSuccess
               ),
               takeUntil(this.destroyed$),
               takeUntil(this.actions$.pipe(ofType(departmentRegisterActions.loadedUrlParams))),
               withLatestFrom(this.store.select(departmentRegisterSelectors.selectEntities))
            )
            .subscribe((entities) => {
               this.findRowForUrlParamId(id, entities);
            });
      } else {
         this.subscribeToSelectedId();
      }

      this.store.pipe(select(selectQuickFilters)).subscribe((filters) => {
         this.includeInactive = filters?.showAllDepartments ?? false;
         this.cdr.detectChanges();
      });
   }

   findRowForUrlParamId(
      id: number,
      entities: [
         LoadPageSuccessProps<DepartmentRegisterListItem> & Action<string>,
         Dictionary<DepartmentRegisterListItem>
      ]
   ): void {
      setTimeout(() => {
         this.store.dispatch(departmentRegisterActions.loadingUrlParams());
         const cell = document.querySelector(`#cell-${id}`);
         const row = cell?.closest('tr') as HTMLTableRowElement | null;

         if (row) {
            this.subscribeToSelectedId();
            this.store.dispatch(departmentRegisterActions.loadedUrlParams({ departmentId: id }));
            this.currentSelectedId = id;
            this.twoColumnLayoutService.setSelectedId(id);
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
         } else if (entities[0]?.hasNextPage) {
            this.store.dispatch(departmentRegisterActions.departmentRegisterAdapterActions.loadPage());
         } else {
            this.router.navigate(['/not-found'], { skipLocationChange: true });
            this.store.dispatch(departmentRegisterActions.loadedUrlParams({ departmentId: id }));
         }
      });
   }

   private subscribeToSelectedId(): void {
      this.twoColumnLayoutService.selectedId$.pipe(takeUntil(this.destroyed$)).subscribe((selectedId) => {
         if (selectedId) {
            const newUrl = `/departments/${selectedId}`;
            this.location.replaceState(newUrl);
         }
      });
   }

   onShowAllDepartmentsChange(includeInactive: boolean): void {
      this.quickFilterChange.emit({ ...this.registerOptions, showAllDepartments: includeInactive });
   }

   onDepartmentClick(departmentId: number) {
      this.selectedId.emit(departmentId);
      this.currentSelectedId = departmentId;
   }

   canDelete(): boolean {
      return this.departmentPrivileges.canDelete;
   }

   onDeleteClick(department: DepartmentRegisterListItem): void {
      this.deleteDepartment.emit(department);
   }

   onDeactivateClick(department: DepartmentRegisterListItem): void {
      this.deactivateDepartment.emit(department);
   }

   onRenameClick(department: DepartmentRegisterListItem): void {
      this.renameDepartment.emit(department);
   }

   onMergeClick(department: DepartmentRegisterListItem): void {
      this.mergeDepartment.emit(department);
   }

   onUntagClick(department: DepartmentRegisterListItem): void {
      this.untagDepartment.emit(department);
   }
}
